/**
 * adsLearned.ts — learned rules with a state a person controls.
 *
 * The system writes a rule when reviewers repeat the same tag (adsFeedback.ts). Each rule is stored once per product and
 * platform and is "assumed" until a person confirms it ("verified") or throws it out ("rejected"). Pure functions decide
 * what is stored and what the AI is told, so the Worker only reads and writes rows.
 *
 *   verified  -> sent as "Rules Sara confirmed"
 *   assumed   -> sent as "Rules the system inferred this week (follow unless the evidence contradicts)", only while the
 *                answers still support it
 *   rejected  -> never sent, and never stored again
 */
import { z } from "zod";
import { adChannelSchema, adProductIdSchema } from "./adsProducts.ts";
import { adFeedbackTagSchema, type AdFeedbackTag } from "./adsFeedback.ts";

export const LEARNED_STATUSES = ["assumed", "verified", "rejected"] as const;
export const learnedStatusSchema = z.enum(LEARNED_STATUSES);
export type LearnedStatus = z.infer<typeof learnedStatusSchema>;
export const LEARNED_STATUS_LABEL: Record<LearnedStatus, string> = { assumed: "Needs review", verified: "Confirmed", rejected: "Rejected" };

export const adLearnedRuleSchema = z.object({
  id: z.string(),
  product: adProductIdSchema,
  channel: adChannelSchema,
  weekStart: z.string(),
  text: z.string(),
  sourceTags: z.array(adFeedbackTagSchema),
  status: learnedStatusSchema,
  verifiedBy: z.string().nullable(),
  verifiedByName: z.string().nullable(),
  verifiedAt: z.string().nullable(),
  createdAt: z.string(),
  /** True when the latest weekly run told the AI this rule. */
  usedInLastRun: z.boolean(),
});
export type AdLearnedRule = z.infer<typeof adLearnedRuleSchema>;

export const adLearnedRulesResponseSchema = z.object({ items: z.array(adLearnedRuleSchema), lastRunAt: z.string().nullable() });
export type AdLearnedRulesResponse = z.infer<typeof adLearnedRulesResponseSchema>;

export const adLearnedRulesQuerySchema = z.object({
  siteId: z.string().min(1).max(100),
  product: adProductIdSchema,
  channel: adChannelSchema,
  status: learnedStatusSchema.optional(),
});
export const adLearnedRuleActionSchema = z.object({ siteId: z.string().min(1).max(100) });
export const adLearnedRulesVerifyAllSchema = z.object({ siteId: z.string().min(1).max(100), product: adProductIdSchema, channel: adChannelSchema });

/** The same rule written with different capitals, spacing or punctuation is the same rule. */
export const normaliseRule = (text: string): string => text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();

export interface InferredRule {
  text: string;
  tag: AdFeedbackTag;
}
export interface StoredRule {
  text: string;
  status: LearnedStatus;
}

/**
 * Which of this week's inferred rules need a new row. A rule that already has a row in any state (assumed, verified or
 * rejected) is not added again, so a confirmed rule stays confirmed and a rejected one stays rejected.
 */
export function rulesToInsert(inferred: readonly InferredRule[], stored: readonly StoredRule[]): InferredRule[] {
  const known = new Set(stored.map((r) => normaliseRule(r.text)));
  const out: InferredRule[] = [];
  for (const r of inferred) {
    const key = normaliseRule(r.text);
    if (!key || known.has(key)) continue;
    known.add(key);
    out.push(r);
  }
  return out;
}

/** What the AI is told: every confirmed rule, and the assumed ones the answers still support. Rejected rules are never included. */
export function rulesForModel(stored: readonly StoredRule[], inferredNow: readonly string[]): { verified: string[]; assumed: string[] } {
  const current = new Set(inferredNow.map(normaliseRule));
  const seen = new Set<string>();
  const pick = (status: LearnedStatus, keep: (key: string) => boolean) =>
    stored.flatMap((r) => {
      const key = normaliseRule(r.text);
      if (r.status !== status || !keep(key) || seen.has(`${status}:${key}`)) return [];
      seen.add(`${status}:${key}`);
      return [r.text];
    });
  const verified = pick("verified", () => true);
  const assumed = pick("assumed", (key) => current.has(key));
  return { verified, assumed };
}

export const VERIFIED_HEADING = "Rules Sara confirmed:";
export const ASSUMED_HEADING = "Rules the system inferred this week (follow unless the evidence contradicts):";

export function renderLearnedRules(rules: { verified: readonly string[]; assumed: readonly string[] }): string {
  const lines: string[] = [];
  if (rules.verified.length > 0) lines.push(VERIFIED_HEADING, ...rules.verified.map((r) => `- ${r}`));
  if (rules.assumed.length > 0) lines.push(ASSUMED_HEADING, ...rules.assumed.map((r) => `- ${r}`));
  return lines.join("\n");
}
